import cuid from "cuid";
import {
  defaults,
  defaultsDeep,
  defaultTo,
  get,
  head,
  isEmpty,
  keyBy,
  map,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  BANK_ACCOUNT_OWNER_TYPE,
  CONTACT_POINT_SYSTEM,
  GENDER,
  KEYS,
  PAYMENT_TYPES,
} from "utils/constants";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import { DEFAULT_ADDRESS_COUNTRY } from "../utils/zipCode";
import View from "./View";

const BankInformation = ({
  project,
  onNext,
  onBack,
  updateUrl,
  currentStatus,
  tracer,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.ACORIS);
  const TRANSLATION = t("BANK_INFORMATION", { returnObjects: true });
  const [initialValues, setInitialValues] = useState(null);
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION, {
    refetchQueries: [
      {
        query: SUBSCRIPTION,
        awaitRefetchQueries: true,
        variables: { where: { id: subscriptionId } },
      },
    ],
  });

  useEffect(() => {
    setInitialValues(null);
  }, [subscriptionId]);

  const initSubscription = useCallback(
    ({ subscription: data }) => {
      const subscription = omitDeep(data, "__typename");
      const debitDays = get(
        subscription,
        "insurancePlan.fields.payment.debitDay",
        [],
      );

      const shouldBeAdherent =
        get(subscription, "payment.payer.owner.isAdherent", true) ||
        get(
          subscription,
          "insurancePlan.fields.payment.shouldBeAdherent",
          false,
        );

      const isPayerReceiver = isEmpty(get(subscription, "payment.receiver.id"))
        ? true
        : get(subscription, "payment.receiver.id") ===
          get(subscription, "payment.payer.id");

      const payer = {
        id: cuid(),
        IBAN: null,
        BIC: null,
        bankName: null,
        bankAddress: null,
        ...subscription.payment?.payer,
        owner: {
          id: cuid(),
          ...subscription.payment?.payer?.owner,
          firstName: defaultTo(
            subscription.payment?.payer?.owner?.firstName,
            shouldBeAdherent
              ? get(subscription, "project.contact.user.firstname")
              : null,
          ),
          lastName: defaultTo(
            subscription.payment?.payer?.owner?.lastName,
            shouldBeAdherent
              ? get(subscription, "project.contact.user.lastname")
              : null,
          ),
          gender: defaultTo(
            subscription.payment?.payer?.owner?.gender,
            shouldBeAdherent
              ? get(subscription, "project.contact.user.gender")
              : null,
          ),
          isAdherent: defaultTo(
            subscription.payment?.payer?.owner?.isAdherent,
            shouldBeAdherent,
          ),
          type: get(subscription, "insurancePlan.fields.tns")
            ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
            : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
        },
      };

      const receiver = isPayerReceiver
        ? { ...payer, owner: { ...payer.owner } }
        : defaultsDeep(subscription.payment?.receiver, {
            id: cuid(),
            IBAN: null,
            BIC: null,
            owner: {
              id: cuid(),
              isAdherent: false,
              gender: null,
            },
          });

      const emptyAddressTelecoms = {
        [CONTACT_POINT_SYSTEM.ADDRESS]: { value: null },
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
        [CONTACT_POINT_SYSTEM.CITY]: { value: null },
        [CONTACT_POINT_SYSTEM.COUNTRY]: { value: DEFAULT_ADDRESS_COUNTRY },
      };

      const initialValues = {
        isPayerReceiver,
        telecoms: {
          payer: defaults(
            keyBy(
              get(subscription, "payment.payer.owner.telecoms", []),
              "system",
            ),
            emptyAddressTelecoms,
          ),
          receiver: defaults(
            keyBy(
              get(subscription, "payment.receiver.owner.telecoms", []),
              "system",
            ),
            emptyAddressTelecoms,
          ),
        },
        payment: {
          id: cuid(),
          contact: get(subscription, "project.contact"),
          ...subscription.payment,
          type:
            get(subscription, "payment.type") ||
            head(get(subscription, "insurancePlan.fields.payment.type")) ||
            PAYMENT_TYPES.DEBIT,
          debitDay: get(
            subscription,
            "payment.debitDay",
            debitDays.length === 1 ? head(debitDays) : null,
          ),
          payer,
          receiver,
        },
      };

      setInitialValues(initialValues);
    },
    [subscriptionId],
  );

  const { data: subscription, loading } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
    onCompleted: initSubscription,
  });

  const data = omitDeep(subscription, "__typename");

  // Check if names match adherent names (case insensitive)
  const checkIfNamesMatchAdherent = (firstName, lastName, gender) => {
    const adherentFirstName = get(project, "contact.user.firstname", "").trim();
    const adherentLastName = get(project, "contact.user.lastname", "").trim();

    const ownerFirstName = (firstName || "").trim();
    const ownerLastName = (lastName || "").trim();

    const firstNameMatch =
      ownerFirstName.toLowerCase() === adherentFirstName.toLowerCase();
    const lastNameMatch =
      ownerLastName.toLowerCase() === adherentLastName.toLowerCase();
    const genderMatch = gender === get(project, "contact.user.gender", "");

    return firstNameMatch && lastNameMatch && (gender ? genderMatch : true);
  };

  const toTelecomsArray = (telecomsMap) =>
    map(telecomsMap, (item, key) =>
      defaults(
        { ...item, value: item?.value != null ? String(item.value) : null },
        { id: cuid(), system: key },
      ),
    );

  const onSubmit = (values) => {
    const payerFirstName = get(values, "payment.payer.owner.firstName", "");
    const payerLastName = get(values, "payment.payer.owner.lastName", "");
    const payerGender = get(values, "payment.payer.owner.gender", "");

    if (payerFirstName && payerLastName) {
      const payerIsAdherent = checkIfNamesMatchAdherent(
        payerFirstName,
        payerLastName,
        payerGender,
      );
      values.payment.payer.owner.isAdherent = payerIsAdherent;
    }

    if (!get(values, "payment.payer.owner.isAdherent", true)) {
      values.payment.payer.owner.telecoms = toTelecomsArray(
        get(values, "telecoms.payer", {}),
      );
    }

    if (values.isPayerReceiver) {
      // Same bank account: keep receiver in sync so a stale adherent
      // receiver does not overwrite third-party payer data on save
      values.payment.receiver = {
        ...values.payment.payer,
        owner: { ...values.payment.payer.owner },
      };
    } else {
      const receiverFirstName = get(
        values,
        "payment.receiver.owner.firstName",
        "",
      );
      const receiverLastName = get(
        values,
        "payment.receiver.owner.lastName",
        "",
      );
      values.payment.receiver.owner.gender =
        get(values, "payment.receiver.owner.gender") || GENDER.MALE;
      if (receiverFirstName && receiverLastName) {
        const receiverIsAdherent = checkIfNamesMatchAdherent(
          receiverFirstName,
          receiverLastName,
        );
        values.payment.receiver.owner.isAdherent = receiverIsAdherent;
      }
    }

    const payment = omitDeep(values.payment, ["__typename", "contact"]);

    return new Promise((resolve, reject) => {
      updateSubscription({
        variables: {
          where: {
            id: subscriptionId,
          },
          data: { payment },
        },
        onCompleted: () => {
          const source = localStorage.getItem(KEYS.SOURCE);
          updateUrl({
            subscriptionId,
            url: get(
              data,
              "subscription.insurancePlan.config.generatedUrl",
              `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
            ),
          });

          Promise.resolve(onNext({ payload: {} }))
            .then(resolve)
            .catch(reject);
        },
        onError: reject,
      });
    });
  };

  if (loading || isEmpty(initialValues)) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        currentStatus={currentStatus}
        tracer={tracer}
        showCart
        showProfile
      />
      <View
        subscription={omitDeep(get(data, "subscription", {}), "__typename")}
        project={project}
        onNext={onSubmit}
        onBack={onBack}
        initialValues={initialValues}
        translate={TRANSLATION}
      />
    </>
  );
};

export default BankInformation;
